import React, { useCallback, useMemo } from 'react'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import { formatRoomType } from 'lib/tours/tourUtils'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import TextButton from 'components/Luxkit/Button/TextButton'
import SlideDown from 'components/Common/SlideDown'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'

const Root = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.M};
  box-shadow: none;
  max-height: unset;
  opacity: 1;
  padding: ${rem(20)};
  transition: 0.35s ease opacity, 0.2s ease box-shadow, 0.2s ease border-color;

  &:not(.is-selected):hover {
    border-color: ${({ theme }) => theme.palette.neutral.default.two};
    box-shadow: ${({ theme }) => theme.shadow.bottom.small};
  }

  & + & {
    margin-top: 0; /* TODO: please ditch child-defined margin! 🙏 */
  }

  &.is-collapsed {
    opacity: 0;
    transition: 0.25s ease opacity, 0.2s ease box-shadow, 0.2s ease border-color;
  }

  &.is-checkout {
   display: flex;
   justify-content: space-between;
  }
`

const RoomOptionCardFooter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)}
`

interface Props {
  index: number
  currentSelectedPrice: number
  isCollapsed?: boolean
  isSelected?: boolean
  price: number
  purchasableOption: Tours.V2OfferPurchasableOption
  onSelect: (purchasableOption: Tours.V2OfferPurchasableOption) => void
  onUnselect: () => void
  isCheckout: boolean
}

function RoomTypeOptionCard(props: Props) {
  const {
    index,
    currentSelectedPrice,
    isCollapsed,
    isSelected,
    price,
    purchasableOption,
    onSelect,
    onUnselect,
    isCheckout,
  } = props

  const roomTypeName = useMemo(
    () => formatRoomType(purchasableOption.roomType),
    [purchasableOption],
  )

  const {
    priceDiff,
    priceDiffIndicator,
    pricePerPerson,
  } = useMemo(
    () => {
      const priceDiff = currentSelectedPrice - price

      return {
        pricePerPerson: price,
        priceDiff: Math.abs(priceDiff),
        priceDiffIndicator: priceDiff === 0 ? '' : priceDiff > 0 ? '-' : '+',
      }
    },
    [currentSelectedPrice, price],
  )

  const handleSelect = useCallback(
    () => {
      onSelect(purchasableOption)
    },
    [onSelect, purchasableOption],
  )

  const handleUnselect = useCallback(
    () => {
      onUnselect()
    },
    [onUnselect],
  )

  return <SlideDown show={!isCollapsed} duration={300}>
    <Root
      type="clean"
      axis="vertical"
      forwardedAs="article"
      className={cn({ 'is-collapsed': isCollapsed, 'is-selected': isSelected, 'is-checkout': isCheckout })}
    >
      <div>
        {isCheckout && <Caption variant="small" weight="bold">{`OPTION ${index + 1}`}</Caption>}
        <Heading variant="heading6" format="titlecase">{roomTypeName}</Heading>
      </div>

      {isCheckout && <PriceContainer>
        <div>
          <Heading variant="heading4" as="span" colour="neutral-one">
            {!priceDiff && <FormatCurrency value={pricePerPerson} format="roundedDollar" />}
            {priceDiff > 0 && <>{priceDiffIndicator}<FormatCurrency value={priceDiff} format="roundedDollar" /></>}
          </Heading>
          <BodyText colour="neutral-two" variant="large" weight="normal" as="span">/person</BodyText>
        </div>
        {isSelected && <TextButton
          kind="primary"
          aria-label={`unselect ${roomTypeName}`}
          onClick={handleUnselect}
          startIcon={<LineCheckIcon />}
          >
          Selected
        </TextButton>}
        {!isSelected && <TextButton
          kind="primary"
          aria-label={`select ${roomTypeName}`}
          onClick={handleSelect}
          >
          Select
        </TextButton>}
      </PriceContainer>}

      {!isCheckout &&
        <RoomOptionCardFooter>
          <Heading variant="heading5">
            {priceDiffIndicator}<FormatCurrency value={priceDiff} />
          </Heading>
          {isSelected && <TextButton
            kind="primary"
            aria-label={`unselect ${roomTypeName}`}
            onClick={handleUnselect}
          >
            Selected
          </TextButton>}
          {!isSelected && <TextButton
            kind="secondary"
            aria-label={`select ${roomTypeName}`}
            onClick={handleSelect}
          >
            Select
          </TextButton>}
        </RoomOptionCardFooter>
      }
    </Root>
  </SlideDown>
}

export default RoomTypeOptionCard
